import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { debounce } from "lodash";
import ChatBubble, {
  ChatBubblePropTypes
} from "components/ai_tools/ChatBubble";
import classNames from "classnames";
import LoadingPill from "../shared/LoadingPill";
import useDetectVisibility from "../shared/hooks/useDetectVisibility";

const ChatArea = ({
  canLoadMore,
  isStreaming,
  loadingPastMessages,
  messages,
  loadMore
}) => {
  // Restore the scroll position when new messages load
  const maintainScrollPosition = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (!loadingPastMessages) return;

    // Preserve scroll position when loading older messages
    const heightDiff = container.scrollHeight - previousScrollHeight.current;
    container.scrollTop = heightDiff;
    // Update stored scroll height
    previousScrollHeight.current = container.scrollHeight;
  };

  const debouncedScrollToBottom = debounce(() => {
    const container = scrollContainerRef.current;
    if (messages.length > 0 && container) {
      const newScrollHeight = container.scrollHeight;

      if (previousScrollHeight.current !== newScrollHeight) {
        previousScrollHeight.current = container.scrollHeight;
        container.scrollTop = container.scrollHeight;
      }
      previousScrollHeight.current = newScrollHeight;
    }
  }, 100);

  const pinScrollToBottom = () => {
    debouncedScrollToBottom();
  };

  const scrollContainerRef = useDetectVisibility(pinScrollToBottom);
  const previousScrollHeight = useRef(0);

  useEffect(() => {
    maintainScrollPosition();
  }, [messages.at(0)]);

  useEffect(() => {
    pinScrollToBottom();
  }, [messages.at(-1), isStreaming]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = debounce(() => {
      if (container.scrollTop === 0) {
        loadMore();
      }
    }, 100);

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [loadMore]);

  return (
    <div className="chat-area--container">
      <div ref={scrollContainerRef} className="chat-area--content">
        {canLoadMore && (
          <LoadingPill
            className={classNames(
              "tw-m-auto tw-mt-[-6px]",
              loadingPastMessages ? "tw-visible" : "tw-invisible"
            )}
          />
        )}
        {messages.map((message) => (
          <ChatBubble {...message} />
        ))}
      </div>
    </div>
  );
};

ChatArea.propTypes = {
  isStreaming: PropTypes.bool,
  loadMore: PropTypes.func,
  loadingPastMessages: PropTypes.bool,
  messages: PropTypes.arrayOf(PropTypes.shape(ChatBubblePropTypes))
};

ChatArea.defaultProps = {
  isStreaming: false,
  loadMore: () => {},
  loadingPastMessages: false,
  messages: []
};

export default ChatArea;
